var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Spin, Typography } from "antd";
import { mergeSnapshotWithFsmInHouseCount, } from '../../utils/buildStoreOverviewSnapshot';
/** 设计稿色值（与用户稿一致） */
var COL_STAY_BAR_ORANGE = "#ff9526";
var COL_PRED_BAR_BLUE = "#8ec9ff";
var COL_TRI_PRE = "#ff85c0";
var COL_TRI_SELL = "#7ed321";
/** 停售 / 维修：与在住条同构，分列在住之前 */
var COL_LOCK_PURPLE = "#722ed1";
/** 维修色条（文字仍用正文色，避免与条同色） */
var COL_REPAIR_BAR = "#f5222d";
/** 每行高度 px（紧凑） */
var ROOM_ROW_H = 14;
/** 渐进条（色块）高度 px */
var ROOM_BAR_INNER_H = 14;
/** 色条圆角 px */
var ROOM_BAR_RADIUS = 2;
/** 房态行尾说明字号 px（与 RoomStatusTextAfter 一致） */
var CHART_DESC_FONT_PX = 11;
/**
 * 绘制区可用宽度 = 轨道宽 − 行尾预留（约 4 个字宽 + 间距），仅用于 unitW，避免色条过长挤掉行尾文案；
 * 各行说明仍紧挨色条，与其它行一致。
 */
var CHART_LABEL_CHAR_SLOTS = 4;
var CHART_LABEL_GAP_AFTER_BAR = 8;
/** 与说明区容器右内边距对齐（见 chart 区块 paddingRight） */
var CHART_LABEL_PAD_RIGHT = 8;
var CHART_LABEL_RESERVE_RIGHT_PX = CHART_LABEL_CHAR_SLOTS * CHART_DESC_FONT_PX +
    CHART_LABEL_GAP_AFTER_BAR +
    CHART_LABEL_PAD_RIGHT;
/** 色条绘制区在「轨道宽 − 预留」基础上再放宽的像素（当前 +60） */
var CHART_DRAWING_WIDTH_BONUS_PX = 60;
/** 与订单流速等单店卡片同宽（GIS 横向滚动行内 flex 子项不随内容无限变宽） */
var CARD_OUTER_WIDTH_PX = 400;
function fmtMoney(v) {
    if (!Number.isFinite(v))
        return "—";
    return new Intl.NumberFormat("zh-CN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(v);
}
function fmtMoneyOrDash(v) {
    if (v == null || Number.isNaN(v))
        return "—";
    return new Intl.NumberFormat("zh-CN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(v);
}
/** 当日预测收入：整数展示，不显示小数 */
function fmtPredictedRevenue(v) {
    if (!Number.isFinite(v))
        return "—";
    return new Intl.NumberFormat("zh-CN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Math.round(v));
}
/** 客源行顺序（设计稿：会员、OTA、前台…） */
var CHANNEL_ROW_ORDER = {
    会员: 0,
    OTA: 1,
    协议: 2,
    中介: 3,
    散客: 4,
};
function channelRowLabel(raw) {
    return raw === "散客" ? "前台" : raw;
}
/** 数字只放在右侧说明，色条内不渲染文字。间数为 0 且换算宽度为 0 时仍显示 1px 细线 */
function RoomStatusColorBar(props) {
    var showZeroLine = props.n <= 0 && props.widthPx <= 0;
    var w = showZeroLine ? 1 : Math.max(0, props.widthPx);
    /** 细线/零宽占位不要被 flex 压缩，否则与下一行同色条左缘对不齐 */
    var flexShrinkBar = showZeroLine || w <= 2 ? 0 : 1;
    return (_jsx("div", { style: {
            marginLeft: props.marginLeftPx,
            width: w,
            minWidth: w > 0 ? 1 : 0,
            height: ROOM_BAR_INNER_H,
            flexShrink: flexShrinkBar,
            background: props.bg,
            borderRadius: ROOM_BAR_RADIUS,
            boxSizing: "border-box",
            overflow: "hidden",
        } }));
}
/** 紧跟在本行色条后的说明（不单独占一列对齐） */
function RoomStatusTextAfter(props) {
    return (_jsx("span", __assign({ style: __assign({ marginLeft: CHART_LABEL_GAP_AFTER_BAR, fontSize: 11, color: "#333333", lineHeight: 1.2, whiteSpace: "nowrap", flexShrink: 0 }, props.style) }, { children: props.children })));
}
/**
 * 量上级容器宽度 → 每间房像素宽 = 容器宽 / roomTotal。
 * 停售、维修：在客源行之前，与在住条同构，先停售再维修、横向收尾相接。
 * 客源：按渠道多行；预离→提前离店横向相连；预抵按渠道多行（与在住同构）；可售接预抵条右侧。
 */
function CascadingRoomStatusChart(_a) {
    var s = _a.s, inHouseChannelsOverride = _a.inHouseChannelsOverride, expectedArrivalChannelsOverride = _a.expectedArrivalChannelsOverride;
    var trackRef = useRef(null);
    var _b = useState(0), trackW = _b[0], setTrackW = _b[1];
    useLayoutEffect(function () {
        var el = trackRef.current;
        if (!el)
            return;
        var measure = function () { return setTrackW(el.clientWidth); };
        measure();
        var ro = new ResizeObserver(measure);
        ro.observe(el);
        return function () { return ro.disconnect(); };
    }, []);
    var channelRows = useMemo(function () {
        var source = inHouseChannelsOverride && inHouseChannelsOverride.length > 0
            ? inHouseChannelsOverride
            : s.stayingChannels;
        var mapped = source
            .map(function (c) {
            var _a, _b;
            return ({
                label: channelRowLabel(c.label),
                value: Math.max(0, c.value),
                sort: (_b = (_a = CHANNEL_ROW_ORDER[c.label]) !== null && _a !== void 0 ? _a : CHANNEL_ROW_ORDER[channelRowLabel(c.label)]) !== null && _b !== void 0 ? _b : 99,
            });
        })
            .filter(function (c) { return c.value > 0; });
        /** FSM 在住 override：按订单来源真实条数降序；报表 stayingChannels 仍用设计稿客源顺序 */
        if (inHouseChannelsOverride && inHouseChannelsOverride.length > 0) {
            return mapped.sort(function (a, b) {
                if (b.value !== a.value)
                    return b.value - a.value;
                return a.label.localeCompare(b.label, "zh-Hans-CN");
            });
        }
        return mapped.sort(function (a, b) { return a.sort - b.sort; });
    }, [s.stayingChannels, inHouseChannelsOverride]);
    var preChannelRows = useMemo(function () {
        if (!(expectedArrivalChannelsOverride === null || expectedArrivalChannelsOverride === void 0 ? void 0 : expectedArrivalChannelsOverride.length))
            return [];
        return expectedArrivalChannelsOverride
            .map(function (c) { return ({
            label: channelRowLabel(c.label),
            value: Math.max(0, c.value),
        }); })
            .filter(function (c) { return c.value > 0; })
            .sort(function (a, b) {
            if (b.value !== a.value)
                return b.value - a.value;
            return a.label.localeCompare(b.label, "zh-Hans-CN");
        });
    }, [expectedArrivalChannelsOverride]);
    var predDepart = Math.max(0, s.predDepart);
    var earlyCk = Math.max(0, s.earlyCheckout);
    var pre = Math.max(0, s.orderRoomTake);
    var sell = Math.max(0, s.sellableTotal);
    var roomTotalN = Math.max(1, s.roomTotal);
    /** 条宽用间数展示：报表维修/锁房若按间夜累计可能偏大，不超过总房量以免条长于轨道 */
    var lockN = Math.max(0, Math.min(s.roomLock, roomTotalN));
    var repairN = Math.max(0, Math.min(s.roomRepair, roomTotalN));
    var effectiveTrackW = trackW > 0
        ? Math.max(0, trackW -
            CHART_LABEL_RESERVE_RIGHT_PX +
            CHART_DRAWING_WIDTH_BONUS_PX)
        : 0;
    var unitW = effectiveTrackW > 0 ? effectiveTrackW / roomTotalN : 0;
    var rows = useMemo(function () {
        if (unitW <= 0)
            return [];
        var out = [];
        var xLockRepair = 0;
        {
            var wPx = Math.max(0, lockN * unitW);
            out.push({
                kind: "lock",
                key: "lock",
                marginLeftPx: xLockRepair,
                widthPx: wPx,
                count: lockN,
            });
            xLockRepair += wPx;
        }
        {
            var wPx = Math.max(0, repairN * unitW);
            out.push({
                kind: "repair",
                key: "repair",
                marginLeftPx: xLockRepair,
                widthPx: wPx,
                count: repairN,
            });
            xLockRepair += wPx;
        }
        var xAfterChannels = xLockRepair;
        for (var i = 0; i < channelRows.length; i++) {
            var ch = channelRows[i];
            var wPx = ch.value * unitW;
            out.push({
                kind: "channel",
                key: "ch-".concat(ch.label, "-").concat(i),
                marginLeftPx: xAfterChannels,
                widthPx: wPx,
                fill: COL_STAY_BAR_ORANGE,
                count: ch.value,
                caption: ch.label,
            });
            xAfterChannels += wPx;
        }
        /**
         * 预离 / 提前离店 / 预抵 / 可售：始终各占一行，间数为 0 时色条为 1px 细线。
         * 条形：预离 → 提前离店（收尾相连）；预抵与预离左对齐；可售接在预抵条右侧。
         */
        var xAlign = xAfterChannels;
        var xAfterPred = xAlign;
        {
            var wPx = Math.max(0, predDepart * unitW);
            out.push({
                kind: "pred",
                key: "pred",
                marginLeftPx: xAlign,
                widthPx: wPx,
                fill: COL_PRED_BAR_BLUE,
                count: predDepart,
                caption: "预离",
            });
            xAfterPred = xAlign + wPx;
        }
        {
            var wPx = Math.max(0, earlyCk * unitW);
            out.push({
                kind: "early",
                key: "early",
                marginLeftPx: predDepart > 0 ? xAfterPred : xAlign,
                widthPx: wPx,
                fill: COL_PRED_BAR_BLUE,
                count: earlyCk,
                caption: "提前离店",
            });
        }
        var preTotalPx = 0;
        if (preChannelRows.length > 0) {
            var xPre = xAlign;
            for (var i = 0; i < preChannelRows.length; i++) {
                var ch = preChannelRows[i];
                var wPx = ch.value * unitW;
                out.push({
                    kind: "preChannel",
                    key: "pre-ch-".concat(ch.label, "-").concat(i),
                    marginLeftPx: xPre,
                    widthPx: wPx,
                    fill: COL_TRI_PRE,
                    count: ch.value,
                    caption: ch.label,
                });
                xPre += wPx;
                preTotalPx += wPx;
            }
        }
        else {
            var preWPx = Math.max(0, pre * unitW);
            preTotalPx = preWPx;
            out.push({
                kind: "pre",
                key: "pre",
                marginLeftPx: xAlign,
                widthPx: preWPx,
                count: pre,
            });
        }
        {
            var wPx = Math.max(0, sell * unitW);
            var sellLeft = preTotalPx > 0 ? xAlign + preTotalPx : xAlign;
            out.push({
                kind: "sell",
                key: "sell",
                marginLeftPx: sellLeft,
                widthPx: wPx,
                count: sell,
            });
        }
        return out;
    }, [
        channelRows,
        lockN,
        repairN,
        predDepart,
        earlyCk,
        pre,
        preChannelRows,
        sell,
        unitW,
    ]);
    var totalChartH = Math.max(ROOM_ROW_H, Math.max(1, rows.length) * ROOM_ROW_H);
    var rowWrap = {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "flex-start",
        width: "100%",
        minWidth: 0,
        height: ROOM_ROW_H,
        minHeight: ROOM_ROW_H,
        boxSizing: "border-box",
        overflow: "visible",
    };
    var renderRow = function (r) {
        if (r.kind === "lock") {
            return (_jsxs("div", __assign({ style: rowWrap }, { children: [_jsx(RoomStatusColorBar, { marginLeftPx: r.marginLeftPx, widthPx: r.widthPx, bg: COL_LOCK_PURPLE, n: r.count }), _jsxs(RoomStatusTextAfter, { children: [_jsx("span", __assign({ style: { color: COL_LOCK_PURPLE } }, { children: "\u505C\u552E" })), r.count] })] }), r.key));
        }
        if (r.kind === "repair") {
            return (_jsxs("div", __assign({ style: rowWrap }, { children: [_jsx(RoomStatusColorBar, { marginLeftPx: r.marginLeftPx, widthPx: r.widthPx, bg: COL_REPAIR_BAR, n: r.count }), _jsxs(RoomStatusTextAfter, { children: ["\u7EF4\u4FEE", r.count] })] }), r.key));
        }
        if (r.kind === "channel") {
            return (_jsxs("div", __assign({ style: rowWrap }, { children: [_jsx(RoomStatusColorBar, { marginLeftPx: r.marginLeftPx, widthPx: r.widthPx, bg: r.fill, n: r.count }), _jsxs(RoomStatusTextAfter, { children: ["\u5728\u4F4F", r.count, "\u30FB", r.caption] })] }), r.key));
        }
        if (r.kind === "pred") {
            return (_jsxs("div", __assign({ style: rowWrap }, { children: [_jsx(RoomStatusColorBar, { marginLeftPx: r.marginLeftPx, widthPx: r.widthPx, bg: r.fill, n: r.count }), _jsxs(RoomStatusTextAfter, { children: [r.caption, r.count] })] }), r.key));
        }
        if (r.kind === "early") {
            return (_jsxs("div", __assign({ style: rowWrap }, { children: [_jsx(RoomStatusColorBar, { marginLeftPx: r.marginLeftPx, widthPx: r.widthPx, bg: r.fill, n: r.count }), _jsxs(RoomStatusTextAfter, { children: [r.caption, r.count] })] }), r.key));
        }
        if (r.kind === "preChannel") {
            return (_jsxs("div", __assign({ style: rowWrap }, { children: [_jsx(RoomStatusColorBar, { marginLeftPx: r.marginLeftPx, widthPx: r.widthPx, bg: r.fill, n: r.count }), _jsxs(RoomStatusTextAfter, { children: ["\u9884\u62B5", r.count, "\u30FB", r.caption] })] }), r.key));
        }
        if (r.kind === "pre") {
            return (_jsxs("div", __assign({ style: rowWrap }, { children: [_jsx(RoomStatusColorBar, { marginLeftPx: r.marginLeftPx, widthPx: r.widthPx, bg: COL_TRI_PRE, n: r.count }), _jsxs(RoomStatusTextAfter, { children: ["\u9884\u62B5", r.count] })] }), r.key));
        }
        if (r.kind === "sell") {
            return (_jsxs("div", __assign({ style: rowWrap }, { children: [_jsx(RoomStatusColorBar, { marginLeftPx: r.marginLeftPx, widthPx: r.widthPx, bg: COL_TRI_SELL, n: r.count }), _jsxs(RoomStatusTextAfter, { children: ["\u53EF\u552E", r.count] })] }), r.key));
        }
        return null;
    };
    return (_jsx("div", __assign({ style: {
            marginTop: 0,
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            overflow: "visible",
        } }, { children: _jsx("div", __assign({ ref: trackRef, style: {
                width: "100%",
                boxSizing: "border-box",
                borderRadius: 0,
                minWidth: 0,
                overflow: "visible",
            } }, { children: trackW <= 0 ? (_jsx("div", { style: { height: totalChartH, background: "#fafafa", borderRadius: 0 } })) : (rows.map(function (r) { return renderRow(r); })) })) })));
}
var StoreOverview = function (_a) {
    var _b, _c;
    var snapshot = _a.snapshot, _d = _a.fsmInHouseCount, fsmInHouseCount = _d === void 0 ? null : _d, _e = _a.fsmExpectedArrivalCount, fsmExpectedArrivalCount = _e === void 0 ? null : _e, predictedRevenueOrderSum = _a.predictedRevenueOrderSum, inHouseChannelCounts = _a.inHouseChannelCounts, expectedArrivalChannelCounts = _a.expectedArrivalChannelCounts, _f = _a.fsmSellableQuantityOpen, fsmSellableQuantityOpen = _f === void 0 ? null : _f, _g = _a.fsmPredDepartCount, fsmPredDepartCount = _g === void 0 ? null : _g, _h = _a.fsmEarlyCheckoutCount, fsmEarlyCheckoutCount = _h === void 0 ? null : _h, _j = _a.loading, loading = _j === void 0 ? false : _j, _k = _a.error, error = _k === void 0 ? null : _k, style = _a.style, onTitleClick = _a.onTitleClick, titleLeftAdornment = _a.titleLeftAdornment, orderTrackingNote = _a.orderTrackingNote;
    var merged = useMemo(function () {
        return mergeSnapshotWithFsmInHouseCount(snapshot, fsmInHouseCount, fsmExpectedArrivalCount, fsmSellableQuantityOpen, fsmPredDepartCount, fsmEarlyCheckoutCount);
    }, [
        snapshot,
        fsmInHouseCount,
        fsmExpectedArrivalCount,
        fsmSellableQuantityOpen,
        fsmPredDepartCount,
        fsmEarlyCheckoutCount,
    ]);
    var predictedRevenueDisplay = predictedRevenueOrderSum !== undefined
        ? predictedRevenueOrderSum
        : merged.predictedRevenue;
    return (_jsxs("section", __assign({ "data-component-id": "card:store-overview", "aria-label": "\u95E8\u5E97\u4FE1\u606F\u6982\u89C8", style: __assign({ width: CARD_OUTER_WIDTH_PX, maxWidth: "100%", flexShrink: 0, height: 300, minHeight: 300, border: "1px solid #f0f0f0", borderRadius: 0, background: "#fff", boxSizing: "border-box", display: "flex", flexDirection: "column", overflow: "hidden" }, style) }, { children: [_jsxs("div", __assign({ style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 12px",
                    borderBottom: "1px solid #f0f0f0",
                    flexShrink: 0,
                } }, { children: [_jsxs("div", __assign({ style: {
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                            flex: 1,
                            gap: 4,
                        } }, { children: [titleLeftAdornment && (_jsx("div", __assign({ style: {
                                    marginLeft: -4,
                                    marginRight: 4,
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                } }, { children: titleLeftAdornment }))), onTitleClick ? (_jsx("button", __assign({ type: "button", onClick: onTitleClick, style: {
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#333",
                                    cursor: "pointer",
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    textAlign: "left",
                                }, title: "\u70B9\u51FB\u67E5\u770B\u95E8\u5E97\u4FE1\u606F\u6982\u89C8\u5206\u6790" }, { children: "\u95E8\u5E97\u4FE1\u606F\u6982\u89C8" }))) : (_jsx("span", __assign({ style: { fontSize: 14, fontWeight: 500, color: "#333" } }, { children: "\u95E8\u5E97\u4FE1\u606F\u6982\u89C8" })))] })), _jsxs("div", __assign({ style: {
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            flexShrink: 0,
                        } }, { children: [_jsxs("span", __assign({ style: { fontSize: 12, color: "#666" } }, { children: ["\u603B", merged.roomTotal, "\u95F4"] })), loading && _jsx(Spin, { size: "small" })] }))] })), _jsxs("div", __assign({ style: {
                    padding: 16,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                } }, { children: [error && (_jsx(Typography.Text, __assign({ type: "danger", style: { fontSize: 11, display: "block", marginBottom: 6, flexShrink: 0 } }, { children: "\u4E1A\u52A1\u62A5\u544A\u52A0\u8F7D\u5931\u8D25\uFF0C\u90E8\u5206\u6307\u6807\u53EF\u80FD\u4E3A\u7A7A" }))), _jsxs("div", __assign({ style: {
                            flexShrink: 0,
                            display: "grid",
                            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                            gridTemplateRows: "auto auto",
                            marginBottom: 8,
                            width: "100%",
                            boxSizing: "border-box",
                            minWidth: 0,
                            overflow: "hidden",
                        } }, { children: [[
                                "平均价",
                                "周环比",
                                "年平均",
                                "当日预测收入",
                            ].map(function (label, i) { return (_jsx("div", __assign({ style: {
                                    borderRight: i < 3 ? "1px solid #ffffff" : undefined,
                                    borderBottom: "1px solid #ffffff",
                                    padding: "6px 8px",
                                    fontSize: 12,
                                    color: "#666666",
                                    lineHeight: 1.35,
                                    background: "#fafafa",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                } }, { children: label }), "h-".concat(label))); }), [
                                fmtMoney(merged.adr),
                                fmtMoneyOrDash(merged.adrWeekCompare),
                                fmtMoneyOrDash(merged.adrYearAvg),
                                fmtPredictedRevenue(predictedRevenueDisplay),
                            ].map(function (val, i) { return (_jsx("div", __assign({ style: {
                                    borderRight: i < 3 ? "1px solid #ffffff" : undefined,
                                    padding: "6px 8px",
                                    fontSize: 16,
                                    fontWeight: 600,
                                    color: "#333333",
                                    lineHeight: 1.25,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                } }, { children: val }), "v-".concat(i))); })] })), _jsx("div", __assign({ style: {
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                            boxSizing: "border-box",
                        } }, { children: _jsx(CascadingRoomStatusChart, { s: merged, inHouseChannelsOverride: inHouseChannelCounts, expectedArrivalChannelsOverride: expectedArrivalChannelCounts }) })), ((_b = merged.orderTrackingNote) !== null && _b !== void 0 ? _b : orderTrackingNote) ? (_jsxs(Typography.Text, __assign({ type: "secondary", style: { fontSize: 10, lineHeight: 1.45, flexShrink: 0, marginTop: 6 } }, { children: ["\u8BA2\u5355\u8DDF\u8E2A\uFF1A", (_c = merged.orderTrackingNote) !== null && _c !== void 0 ? _c : orderTrackingNote] }))) : null] }))] })));
};
export default StoreOverview;
